// Typed component footprints for the printed inductive encoder board (Task 7).
//
// Pure TypeScript. No Angular / DI imports. Deterministic (no Date/random).
// Angles are radians; math frame is y-up. These are the FIRST components in the
// whole PCB-stator project — the stator/target boards are copper-only, this
// encoder ring actually carries the RAA2P3226 chip, its tank / decoupling
// passives, and the output header.
//
// A Footprint is a package-local pad cluster placed at `at` and rotated by
// `rotRad`. Pad geometry is authored in the package's own frame (origin at the
// body center, pin-1 conventionally top-left) and then rigidly transformed to
// the board frame — so a caller only picks a location + orientation and a
// per-pin net map, never raw pad coordinates.
//
// Standard package dimensions (from the task brief):
//   TSSOP-16 : body 5.0 x 4.4 mm, 0.65 mm pitch, two rows 4.4 mm apart,
//              pad 0.4 x 1.5 mm (0.4 across pitch, 1.5 reaching outward).
//   0603     : two pads 0.9 x 0.95 mm on 1.6 mm centers.
//   header   : 2.54 mm pitch, 1.7 mm round ring, 1.0 mm plated drill.
//
// `silk` is a courtyard: a closed polyline that SURROUNDS every pad (it never
// crosses one) so the assembly outline is legible without shorting the mask.

import { EncoderNet } from './pcb-stator-encoder-chip';

export interface FootprintPad {
  net: EncoderNet | null;
  at: [number, number];
  wMm: number;
  hMm: number;
  drillMm?: number;          // present => plated through-hole (header); absent => SMD
}

export interface Footprint {
  refdes: string;
  pads: FootprintPad[];
  silk: Array<Array<[number, number]>>;   // closed courtyard polyline(s)
}

// ── Package dimensions ─────────────────────────────────────────────────────
export const TSSOP16_PITCH_MM = 0.65;
export const TSSOP16_ROW_SPACING_MM = 4.4;   // center-to-center of the two pad rows
export const TSSOP16_PAD_W_MM = 1.5;         // reaches outward from the body (local x)
export const TSSOP16_PAD_H_MM = 0.3;         // along the pitch (local y). 0.65 pitch − 0.30 = 0.35 mm
                                             // adjacent-pad gap, clearing the 0.3 mm copper rule the raster
                                             // DRC enforces (Task 8) with margin: a 0.4 mm pad left only a
                                             // 0.25 mm gap between every adjacent different-net pin — a real
                                             // sub-clearance finding fixed here in geometry, not by widening
                                             // the DRC (0.35 mm also clears the raster's seed-landing radius).
const TSSOP16_PINS_PER_ROW = 8;

export const P0603_PAD_W_MM = 0.9;
export const P0603_PAD_H_MM = 0.95;
export const P0603_CENTERS_MM = 1.6;

export const HEADER_PITCH_MM = 2.54;
export const HEADER_RING_MM = 1.7;
export const HEADER_DRILL_MM = 1.0;

const COURTYARD_MARGIN_MM = 0.25;

type Pt = [number, number];

/** Rigid transform: rotate a package-frame point by `rotRad`, then translate to
 *  the placement `at`. The single choke-point every footprint builder funnels
 *  its authored geometry through, so "place + orient" is the only knob. */
function place(local: Pt, at: Pt, rotRad: number): Pt {
  const c = Math.cos(rotRad);
  const s = Math.sin(rotRad);
  return [at[0] + local[0] * c - local[1] * s, at[1] + local[0] * s + local[1] * c];
}

/** Axis-aligned courtyard rectangle (package frame) enclosing every pad by
 *  `margin`, returned as a closed, transformed polyline. */
function courtyard(halfX: number, halfY: number, at: Pt, rotRad: number): Array<Pt> {
  const corners: Pt[] = [
    [-halfX, -halfY], [halfX, -halfY], [halfX, halfY], [-halfX, halfY], [-halfX, -halfY],
  ];
  return corners.map(p => place(p, at, rotRad));
}

/** TSSOP-16: 16 pads, 0.65 mm pitch, two rows 4.4 mm apart. Pin 1 is top-left
 *  in the package frame (min local x, max local y); pins 1-8 run DOWN the left
 *  row, pins 9-16 run UP the right row (standard CCW IC numbering). Every pad
 *  is mapped to the chip net `netByPin` returns for its pin number. */
export function tssop16(at: [number, number], rotRad: number, netByPin: (pin: number) => EncoderNet): Footprint {
  const halfRow = TSSOP16_ROW_SPACING_MM / 2;                 // 2.2
  const topY = ((TSSOP16_PINS_PER_ROW - 1) / 2) * TSSOP16_PITCH_MM;  // 2.275
  const pads: FootprintPad[] = [];

  for (let k = 0; k < TSSOP16_PINS_PER_ROW; k++) {
    // Left row, pins 1..8, top -> bottom.
    const pin = k + 1;
    const local: Pt = [-halfRow, topY - k * TSSOP16_PITCH_MM];
    pads.push({ net: netByPin(pin), at: place(local, at, rotRad), wMm: TSSOP16_PAD_W_MM, hMm: TSSOP16_PAD_H_MM });
  }
  for (let k = 0; k < TSSOP16_PINS_PER_ROW; k++) {
    // Right row, pins 9..16, bottom -> top.
    const pin = TSSOP16_PINS_PER_ROW + 1 + k;
    const local: Pt = [halfRow, -topY + k * TSSOP16_PITCH_MM];
    pads.push({ net: netByPin(pin), at: place(local, at, rotRad), wMm: TSSOP16_PAD_W_MM, hMm: TSSOP16_PAD_H_MM });
  }

  const halfX = halfRow + TSSOP16_PAD_W_MM / 2 + COURTYARD_MARGIN_MM;
  const halfY = topY + TSSOP16_PAD_H_MM / 2 + COURTYARD_MARGIN_MM;
  return { refdes: 'U1', pads, silk: [courtyard(halfX, halfY, at, rotRad)] };
}

/** 0603 two-pad passive. Pad 0 (net[0]) sits at local -x, pad 1 (net[1]) at
 *  local +x, on 1.6 mm centers. */
export function passive0603(at: [number, number], rotRad: number, nets: [EncoderNet, EncoderNet], refdes: string): Footprint {
  const half = P0603_CENTERS_MM / 2;
  const pads: FootprintPad[] = [
    { net: nets[0], at: place([-half, 0], at, rotRad), wMm: P0603_PAD_W_MM, hMm: P0603_PAD_H_MM },
    { net: nets[1], at: place([half, 0], at, rotRad), wMm: P0603_PAD_W_MM, hMm: P0603_PAD_H_MM },
  ];
  const halfX = half + P0603_PAD_W_MM / 2 + COURTYARD_MARGIN_MM;
  const halfY = P0603_PAD_H_MM / 2 + COURTYARD_MARGIN_MM;
  return { refdes, pads, silk: [courtyard(halfX, halfY, at, rotRad)] };
}

/** 1x N 2.54 mm header. Pins run along local x, centered on the origin; pad i
 *  carries nets[i]. Each pad is a plated through-hole (1.7 mm ring, 1.0 mm
 *  drill) so its drill lands in the board's plated drill set. */
export function header254(at: [number, number], rotRad: number, nets: EncoderNet[], refdes: string): Footprint {
  const n = nets.length;
  const pads: FootprintPad[] = nets.map((net, i) => {
    const x = (i - (n - 1) / 2) * HEADER_PITCH_MM;
    return { net, at: place([x, 0], at, rotRad), wMm: HEADER_RING_MM, hMm: HEADER_RING_MM, drillMm: HEADER_DRILL_MM };
  });
  const halfX = ((n - 1) / 2) * HEADER_PITCH_MM + HEADER_RING_MM / 2 + COURTYARD_MARGIN_MM;
  const halfY = HEADER_RING_MM / 2 + COURTYARD_MARGIN_MM;
  return { refdes, pads, silk: [courtyard(halfX, halfY, at, rotRad)] };
}
